import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  DndContext, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors,
  useDraggable, useDroppable, DragOverlay, closestCorners,
} from "@dnd-kit/core";
import {
  DollarSign, Wallet, Percent, Timer, CalendarDays, FileText, TrendingUp, Layers,
  Columns3, Rows3, Search, GripVertical, AlarmClock, Inbox, User, ArrowRight,
} from "lucide-react";
import {
  STAGE_META, STAGE_ORDER, WON_STAGES, PRIORITY_META, money, fmtDate, isOverdue, acv,
} from "./crmMeta";
import LeadDrawer from "./LeadDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- small pieces ---------- */
function PriorityBadge({ p }) {
  const m = PRIORITY_META[p] || PRIORITY_META.medium;
  return <span className="text-[10px] font-700 rounded px-1.5 py-0.5" style={{ background: `${m.color}22`, color: m.color }}>{m.label}</span>;
}

function LeadCard({ lead, onOpen, dragHandle }) {
  const overdue = isOverdue(lead);
  const m = STAGE_META[lead.stage];
  return (
    <div data-testid={`lead-card-${lead.id}`} onClick={() => onOpen(lead)}
      className={`glass rounded-2xl p-3.5 cursor-pointer transition-shadow hover:shadow-lg ${overdue ? "ring-1 ring-red-500/60" : ""}`}
      style={{ borderLeft: `3px solid ${m.color}` }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-white font-600 text-sm truncate">{lead.company_name}</div>
          <div className="text-white/45 text-xs truncate">{lead.contact_person || "—"}</div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <PriorityBadge p={lead.priority} />
          {dragHandle}
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="font-serif text-lg text-white" style={{ fontWeight: 500 }}>{money(acv(lead))}<span className="text-white/35 text-xs font-sans">/yr</span></span>
        <span className="text-white/40 text-[11px]">{money(lead.estimated_monthly_value)}/mo</span>
      </div>
      {lead.next_action && <div className="mt-2 text-white/55 text-xs flex items-center gap-1"><ArrowRight className="h-3 w-3 text-steel-light" /> {lead.next_action}</div>}
      <div className="mt-2 flex items-center justify-between">
        <span className="text-white/45 text-[11px] flex items-center gap-1"><User className="h-3 w-3" />{(lead.assigned_to || "").split(" ")[0]}</span>
        {lead.follow_up_date && (
          <span className={`text-[11px] flex items-center gap-1 ${overdue ? "text-red-400 font-600" : "text-white/45"}`}>
            <AlarmClock className="h-3 w-3" />{fmtDate(lead.follow_up_date)}
          </span>
        )}
      </div>
    </div>
  );
}

function DraggableLead({ lead, onOpen }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: lead.id, data: { stage: lead.stage } });
  const handle = (
    <button ref={setNodeRef} {...attributes} {...listeners} data-testid={`drag-handle-${lead.id}`}
      onClick={(e) => e.stopPropagation()} aria-label={`Move ${lead.company_name}`}
      className="text-white/30 hover:text-white/70 cursor-grab active:cursor-grabbing touch-none focus:outline-none focus:ring-2 focus:ring-steel-light rounded">
      <GripVertical className="h-4 w-4" />
    </button>
  );
  return <div style={{ opacity: isDragging ? 0.35 : 1 }}><LeadCard lead={lead} onOpen={onOpen} dragHandle={handle} /></div>;
}

function BoardColumn({ stage, leads, onOpen }) {
  const m = STAGE_META[stage];
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  return (
    <div data-testid={`board-col-${stage}`} className="w-72 flex-shrink-0">
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: m.color }} />
          <span className="text-white text-sm font-600">{m.label}</span>
        </div>
        <span className="text-xs rounded-full px-2 py-0.5 font-600" style={{ background: `${m.color}22`, color: m.color }}>{leads.length}</span>
      </div>
      <div ref={setNodeRef} className={`space-y-3 rounded-2xl p-2 min-h-[120px] transition-colors ${isOver ? "ring-2" : ""}`}
        style={{ background: isOver ? `${m.color}18` : `${m.color}0a`, ...(isOver ? { boxShadow: `inset 0 0 0 2px ${m.color}` } : {}) }}>
        {leads.map((l) => <DraggableLead key={l.id} lead={l} onOpen={onOpen} />)}
      </div>
    </div>
  );
}

/* ---------- summary ---------- */
function Metric({ icon: Icon, label, value, tone }) {
  return (
    <div className="glass rounded-3xl p-5" data-testid={`metric-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="h-9 w-9 rounded-xl flex items-center justify-center mb-3" style={{ background: `${tone}22` }}><Icon className="h-4 w-4" style={{ color: tone }} /></div>
      <div className="font-serif text-2xl md:text-3xl text-white" style={{ fontWeight: 500 }}>{value}</div>
      <div className="text-white/45 text-xs mt-1">{label}</div>
    </div>
  );
}

/* ---------- main ---------- */
export default function CrmLeads({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [team, setTeam] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("board");
  const [q, setQ] = useState("");
  const [fPriority, setFPriority] = useState("");
  const [fAssigned, setFAssigned] = useState("");
  const [fOverdue, setFOverdue] = useState(false);
  const [selected, setSelected] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [confirmMove, setConfirmMove] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const loadLeads = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/leads`, auth); setLeads(r.data); } catch { setLeads([]); }
  }, []); // eslint-disable-line
  const loadSummary = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/pipeline-summary`, auth); setSummary(r.data); } catch { /* ignore */ }
  }, []); // eslint-disable-line
  const loadFollowups = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/followups/today`, auth); setFollowups(r.data); } catch { /* ignore */ }
  }, []); // eslint-disable-line

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadLeads(), loadSummary(), loadFollowups(),
        axios.get(`${API}/admin/team`, auth).then((r) => setTeam(r.data)).catch(() => {})]);
      setLoading(false);
    })();
  }, [loadLeads, loadSummary, loadFollowups]);

  const refresh = () => { loadLeads(); loadSummary(); loadFollowups(); };

  const applyStage = async (leadId, toStage) => {
    const prev = leads.find((l) => l.id === leadId)?.stage;
    if (prev === toStage) return;
    setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, stage: toStage } : l)));
    try {
      await axios.patch(`${API}/admin/leads/${leadId}/stage?stage=${toStage}`, {}, auth);
      loadSummary(); loadFollowups();
      toast.success(`Moved to ${STAGE_META[toStage].label}`);
    } catch {
      setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, stage: prev } : l)));
      toast.error("Couldn't move lead. Reverted.");
    }
  };

  const onDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over) return;
    const from = active.data.current?.stage;
    const to = over.id;
    if (!STAGE_META[to] || from === to) return;
    if (STAGE_ORDER.indexOf(to) < STAGE_ORDER.indexOf(from)) {
      setConfirmMove({ leadId: active.id, from, to });
    } else {
      applyStage(active.id, to);
    }
  };

  const filtered = leads.filter((l) => {
    if (q && !`${l.company_name} ${l.contact_person} ${l.lead_id}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (fPriority && l.priority !== fPriority) return false;
    if (fAssigned && l.assigned_to !== fAssigned) return false;
    if (fOverdue && !isOverdue(l)) return false;
    return true;
  });

  const M = summary?.metrics || {};
  const metricCards = [
    { icon: Layers, label: "Total Pipeline Value", value: money(M.pipeline_value), tone: "#7FA8C9" },
    { icon: TrendingUp, label: "Weighted Forecast", value: money(M.weighted_forecast), tone: "#8B5CF6" },
    { icon: Wallet, label: "Expected MRR", value: money(M.expected_mrr), tone: "#059669" },
    { icon: DollarSign, label: "Expected ARR", value: money(M.expected_arr), tone: "#22C55E" },
    { icon: DollarSign, label: "Avg Deal Size", value: money(M.avg_deal_size), tone: "#14B8A6" },
    { icon: Percent, label: "Win Rate", value: `${M.win_rate ?? 0}%`, tone: "#4ADE80" },
    { icon: Timer, label: "Sales Cycle", value: `${M.sales_cycle_length ?? 0} days`, tone: "#F59E0B" },
    { icon: FileText, label: "Outstanding Invoices", value: money(M.outstanding_balance), tone: "#F97316" },
  ];

  if (loading) return <div className="glass rounded-3xl p-16 text-center text-white/40">Loading CRM…</div>;

  const activeLead = leads.find((l) => l.id === activeId);

  return (
    <div className="space-y-6" data-testid="crm-leads">
      {/* Today's follow-ups */}
      {followups.length > 0 && (
        <div className="glass rounded-3xl p-5 border border-red-500/20" data-testid="followups-widget">
          <div className="flex items-center gap-2 mb-3"><AlarmClock className="h-4 w-4 text-red-400" /><p className="overline text-red-300">Today’s Follow-ups · {followups.length}</p></div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {followups.map((l) => (
              <button key={l.id} data-testid={`followup-${l.id}`} onClick={() => setSelected(l)}
                className="flex-shrink-0 glass rounded-2xl px-4 py-2.5 text-left hover:bg-white/10 transition-colors">
                <div className="text-white text-sm font-600">{l.company_name}</div>
                <div className={`text-xs ${l.overdue ? "text-red-400" : "text-white/45"}`}>{l.overdue ? "Overdue · " : ""}{fmtDate(l.follow_up_date)} · {l.next_action || ""}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Financial metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pipeline-metrics">
        {metricCards.map((c) => <Metric key={c.label} {...c} />)}
      </div>

      {/* Stage counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pipeline-stage-summary">
        {STAGE_ORDER.map((k) => {
          const m = STAGE_META[k]; const Icon = m.icon;
          const count = summary?.stage_counts?.[k] ?? 0;
          return (
            <div key={k} data-testid={`stage-count-${k}`} className="glass rounded-2xl p-4 relative overflow-hidden">
              <div className="absolute left-0 top-0 h-full w-1" style={{ background: m.color }} />
              <div className="flex items-center gap-2 mb-2">
                <span className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ background: `${m.color}22` }}><Icon className="h-3.5 w-3.5" style={{ color: m.color }} /></span>
                <span className="text-white/55 text-xs leading-tight">{m.label}</span>
              </div>
              <div className="font-serif text-3xl text-white" style={{ fontWeight: 500 }}>{count}</div>
            </div>
          );
        })}
      </div>

      {/* Filters + view toggle */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="crm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…"
              className="bg-white/[0.03] border border-white/10 rounded-full pl-9 pr-4 py-2 text-white text-sm placeholder:text-white/35 focus:outline-none focus:border-steel-light/60 w-52" />
          </div>
          <select data-testid="filter-priority" value={fPriority} onChange={(e) => setFPriority(e.target.value)} className="bg-white/[0.03] border border-white/10 rounded-full px-3 py-2 text-white/70 text-xs focus:outline-none">
            <option value="" className="bg-ink-800">All priorities</option>
            {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k} className="bg-ink-800">{v.label}</option>)}
          </select>
          <select data-testid="filter-assigned" value={fAssigned} onChange={(e) => setFAssigned(e.target.value)} className="bg-white/[0.03] border border-white/10 rounded-full px-3 py-2 text-white/70 text-xs focus:outline-none">
            <option value="" className="bg-ink-800">All owners</option>
            {team.map((t) => <option key={t.id} value={t.name} className="bg-ink-800">{t.name}</option>)}
          </select>
          <button data-testid="filter-overdue" onClick={() => setFOverdue((v) => !v)}
            className={`rounded-full px-3 py-2 text-xs font-600 transition-colors ${fOverdue ? "bg-red-500/20 text-red-300" : "glass text-white/55 hover:text-white"}`}>Overdue</button>
        </div>
        <div className="flex items-center gap-1 glass rounded-full p-1">
          <button data-testid="view-board" onClick={() => setView("board")} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-600 ${view === "board" ? "bg-white text-ink-950" : "text-white/55 hover:text-white"}`}><Columns3 className="h-3.5 w-3.5" /> Board</button>
          <button data-testid="view-table" onClick={() => setView("table")} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-600 ${view === "table" ? "bg-white text-ink-950" : "text-white/55 hover:text-white"}`}><Rows3 className="h-3.5 w-3.5" /> Table</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="glass rounded-3xl p-16 text-center text-white/40" data-testid="empty-state"><Inbox className="h-10 w-10 mx-auto mb-4 opacity-40" />No leads match your filters.</div>
      ) : view === "board" ? (
        <DndContext sensors={sensors} collisionDetection={closestCorners} autoScroll={{ threshold: { x: 0.2, y: 0 } }} onDragStart={({ active }) => setActiveId(active.id)} onDragEnd={onDragEnd} onDragCancel={() => setActiveId(null)}>
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
            {STAGE_ORDER.map((k) => <BoardColumn key={k} stage={k} leads={filtered.filter((l) => (STAGE_META[l.stage] ? l.stage : "new_lead") === k)} onOpen={setSelected} />)}
          </div>
          <DragOverlay>{activeLead ? <div className="w-72"><LeadCard lead={activeLead} onOpen={() => {}} dragHandle={<GripVertical className="h-4 w-4 text-white/40" />} /></div> : null}</DragOverlay>
        </DndContext>
      ) : (
        <div className="glass rounded-3xl overflow-hidden overflow-x-auto" data-testid="pipeline-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead><tr className="text-white/40 text-xs border-b border-white/8">
              {["Company", "Stage", "Owner", "Value (ACV)", "Priority", "Follow-up", "Next Action"].map((h) => <th key={h} className="text-left font-500 px-4 py-3">{h}</th>)}
            </tr></thead>
            <tbody>
              {filtered.map((l) => {
                const m = STAGE_META[l.stage] || STAGE_META.new_lead; const overdue = isOverdue(l);
                return (
                  <tr key={l.id} data-testid={`row-${l.id}`} onClick={() => setSelected(l)} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer">
                    <td className="px-4 py-3"><div className="text-white font-600">{l.company_name}</div><div className="text-white/40 text-xs">{l.contact_person}</div></td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-600" style={{ background: `${m.color}22`, color: m.color }}><m.icon className="h-3 w-3" />{m.label}</span></td>
                    <td className="px-4 py-3 text-white/70">{l.assigned_to}</td>
                    <td className="px-4 py-3 text-white font-600">{money(acv(l))}</td>
                    <td className="px-4 py-3"><PriorityBadge p={l.priority} /></td>
                    <td className={`px-4 py-3 ${overdue ? "text-red-400 font-600" : "text-white/60"}`}>{l.follow_up_date ? fmtDate(l.follow_up_date) : "—"}</td>
                    <td className="px-4 py-3 text-white/60">{l.next_action || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && <LeadDrawer lead={leads.find((l) => l.id === selected.id) || selected} team={team} token={token} onClose={() => setSelected(null)} onSaved={refresh} />}

      {confirmMove && (
        <div data-testid="backward-confirm" className="fixed inset-0 z-[80] bg-ink-950/70 backdrop-blur-md flex items-center justify-center px-6" onClick={() => setConfirmMove(null)}>
          <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="glass rounded-4xl max-w-md w-full p-8 text-center">
            <h3 className="font-serif text-2xl text-white mb-2">Move backward?</h3>
            <p className="text-white/55 text-sm mb-6">You’re moving this lead from <span className="text-white font-600">{STAGE_META[confirmMove.from].label}</span> back to <span className="text-white font-600">{STAGE_META[confirmMove.to].label}</span>. Are you sure?</p>
            <div className="flex gap-3 justify-center">
              <button data-testid="confirm-cancel" onClick={() => setConfirmMove(null)} className="btn-pill glass text-white/70 px-6 py-3 text-sm font-600">Cancel</button>
              <button data-testid="confirm-move" onClick={() => { applyStage(confirmMove.leadId, confirmMove.to); setConfirmMove(null); }} className="btn-pill bg-white text-ink-950 px-6 py-3 text-sm font-600">Yes, move back</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
